import { useSEO } from "../hooks/useSEO";

export default function Services() {
  useSEO({
    title: "Software Development & Cloud Services | Qicino Limited Nigeria",
    description: "Qicino Limited offers professional software development, custom applications, mobile apps, cloud migration (AWS/Azure/Google Cloud), and digital transformation services for Nigerian and African businesses.",
    keywords: "Qicino Limited, software development Nigeria, custom software, cloud services, AWS Azure Google Cloud, mobile app development, digital transformation, web development services",
    url: "https://qicino.com/services",
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-50 to-blue-50 py-16">
        <div className="container mx-auto px-6 py-16">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Professional Services
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive software development and technology solutions to
              accelerate your business growth and digital transformation
            </p>
          </div>
        </div>
      </section>

      {/* Main Services Grid */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {/* Software Development */}
            <div className="bg-green-50 p-8 rounded-lg border border-green-200 hover:shadow-lg transition-shadow">
              <div className="h-12 w-12 bg-green-500 rounded-lg flex items-center justify-center mb-6">
                <svg
                  className="h-6 w-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900">
                Software Development
              </h3>
              <p className="text-gray-700 mb-4">
                Custom software solutions tailored to your business needs. From
                enterprise applications to specialized tools.
              </p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• Enterprise Software Solutions</li>
                <li>• Business Process Automation</li>
                <li>• Database Management Systems</li>
                <li>• API Development & Integration</li>
              </ul>
            </div>

            {/* App Development */}
            <div className="bg-blue-50 p-8 rounded-lg border border-blue-200 hover:shadow-lg transition-shadow">
              <div className="h-12 w-12 bg-blue-500 rounded-lg flex items-center justify-center mb-6">
                <svg
                  className="h-6 w-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900">
                App Development
              </h3>
              <p className="text-gray-700 mb-4">
                Native and cross-platform mobile applications for iOS and
                Android platforms with exceptional user experiences.
              </p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• iOS & Android Native Apps</li>
                <li>• React Native Development</li>
                <li>• Progressive Web Apps (PWA)</li>
                <li>• App Store Optimization</li>
              </ul>
            </div>

            {/* Cloud Based Solutions */}
            <div className="bg-purple-50 p-8 rounded-lg border border-purple-200 hover:shadow-lg transition-shadow">
              <div className="h-12 w-12 bg-purple-500 rounded-lg flex items-center justify-center mb-6">
                <svg
                  className="h-6 w-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900">
                Cloud Based Solutions
              </h3>
              <p className="text-gray-700 mb-4">
                Scalable cloud infrastructure and services to power your
                business operations with reliability and efficiency.
              </p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• Cloud Architecture Design</li>
                <li>• SaaS Application Development</li>
                <li>• Multi-tenant Solutions</li>
                <li>• Cloud Security Implementation</li>
              </ul>
            </div>

            {/* UI/UX Design */}
            <div className="bg-orange-50 p-8 rounded-lg border border-orange-200 hover:shadow-lg transition-shadow">
              <div className="h-12 w-12 bg-orange-500 rounded-lg flex items-center justify-center mb-6">
                <svg
                  className="h-6 w-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM7 3H5a2 2 0 00-2 2v12a4 4 0 004 4h2a2 2 0 002-2V5a2 2 0 00-2-2H7z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900">
                UI/UX Design
              </h3>
              <p className="text-gray-700 mb-4">
                User-centered design solutions that create intuitive, engaging,
                and conversion-optimized digital experiences.
              </p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• User Experience Research</li>
                <li>• Interface Design & Prototyping</li>
                <li>• Design System Creation</li>
                <li>• Usability Testing & Optimization</li>
              </ul>
            </div>

            {/* Cloud Migration */}
            <div className="bg-indigo-50 p-8 rounded-lg border border-indigo-200 hover:shadow-lg transition-shadow">
              <div className="h-12 w-12 bg-indigo-500 rounded-lg flex items-center justify-center mb-6">
                <svg
                  className="h-6 w-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900">
                Cloud Migration
              </h3>
              <p className="text-gray-700 mb-4">
                Seamless migration of your existing systems to cloud platforms
                with minimal downtime and maximum efficiency.
              </p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• Migration Strategy & Planning</li>
                <li>• Data Migration Services</li>
                <li>• Legacy System Modernization</li>
                <li>• Post-Migration Optimization</li>
              </ul>
            </div>

            {/* Continuous Support */}
            <div className="bg-teal-50 p-8 rounded-lg border border-teal-200 hover:shadow-lg transition-shadow">
              <div className="h-12 w-12 bg-teal-500 rounded-lg flex items-center justify-center mb-6">
                <svg
                  className="h-6 w-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900">
                Continuous Support
              </h3>
              <p className="text-gray-700 mb-4">
                24/7 technical support and maintenance services to ensure your
                systems run smoothly and efficiently.
              </p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• 24/7 Technical Support</li>
                <li>• System Monitoring & Maintenance</li>
                <li>• Performance Optimization</li>
                <li>• Security Updates & Patches</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Why Choose Qicino Limited?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We combine technical expertise with business understanding to
              deliver solutions that drive real results
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 mb-16">
            <div className="text-center bg-white p-8 rounded-lg shadow-sm">
              <div className="h-16 w-16 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-6">
                <svg
                  className="h-8 w-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900">
                Proven Expertise
              </h3>
              <p className="text-gray-700">
                Our team of certified professionals brings years of experience
                in software development and technology solutions.
              </p>
            </div>

            <div className="text-center bg-white p-8 rounded-lg shadow-sm">
              <div className="h-16 w-16 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-6">
                <svg
                  className="h-8 w-8 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900">
                Rapid Development
              </h3>
              <p className="text-gray-700">
                We deliver projects efficiently without compromising on quality,
                getting you to market faster than competitors.
              </p>
            </div>

            <div className="text-center bg-white p-8 rounded-lg shadow-sm">
              <div className="h-16 w-16 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-6">
                <svg
                  className="h-8 w-8 text-orange-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900">
                Ongoing Support
              </h3>
              <p className="text-gray-700">
                We provide continuous support and maintenance to ensure your
                systems run smoothly and efficiently.
              </p>
            </div>
          </div>

          {/* Additional Services */}
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-sm">
              <h3 className="text-2xl font-semibold mb-4 text-gray-900">
                Managed Support Services
              </h3>
              <p className="text-gray-700 mb-4">
                Comprehensive managed services to keep your technology
                infrastructure running optimally while you focus on your core
                business.
              </p>
              <ul className="text-gray-600 space-y-2">
                <li>• Infrastructure Monitoring</li>
                <li>• Proactive Maintenance</li>
                <li>• Security Management</li>
                <li>• Performance Optimization</li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm">
              <h3 className="text-2xl font-semibold mb-4 text-gray-900">
                Site Management
              </h3>
              <p className="text-gray-700 mb-4">
                End-to-end website and application management services ensuring
                optimal performance, security, and user experience.
              </p>
              <ul className="text-gray-600 space-y-2">
                <li>• Content Management</li>
                <li>• Security Updates</li>
                <li>• Performance Monitoring</li>
                <li>• Backup & Recovery</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Flexible Services Section */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Flexible Service Options
            </h2>
            <p className="text-lg text-gray-600">
              We adapt our services to meet your specific needs and budget
              requirements
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-8 rounded-lg border border-green-200">
              <h3 className="text-xl font-semibold mb-4 text-gray-900">
                Project-Based
              </h3>
              <p className="text-gray-700 mb-4">
                Fixed-scope projects with defined deliverables and timelines.
                Perfect for specific development needs.
              </p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• Clear project scope</li>
                <li>• Fixed pricing</li>
                <li>• Defined timeline</li>
                <li>• Dedicated team</li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-8 rounded-lg border border-blue-200">
              <h3 className="text-xl font-semibold mb-4 text-gray-900">
                Retainer Services
              </h3>
              <p className="text-gray-700 mb-4">
                Ongoing partnership with monthly retainer for continuous
                development and support needs.
              </p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• Priority support</li>
                <li>• Flexible scope</li>
                <li>• Cost-effective</li>
                <li>• Long-term partnership</li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-8 rounded-lg border border-orange-200">
              <h3 className="text-xl font-semibold mb-4 text-gray-900">
                Custom Solutions
              </h3>
              <p className="text-gray-700 mb-4">
                Tailored service packages designed specifically for your unique
                business requirements and goals.
              </p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• Customized approach</li>
                <li>• Scalable solutions</li>
                <li>• Flexible terms</li>
                <li>• Business-focused</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Umbrella Business Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Our Business Ecosystem
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Beyond software development, Qicino Limited serves as an
              umbrella organization for multiple business verticals
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <div className="h-16 w-16 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-6">
                <svg
                  className="h-8 w-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900 text-center">
                Real Estate Services
              </h3>
              <p className="text-gray-700 text-center">
                Comprehensive real estate solutions including property
                management, sales platforms, and digital property services.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <div className="h-16 w-16 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-6">
                <svg
                  className="h-8 w-8 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900 text-center">
                Automobile Sales
              </h3>
              <p className="text-gray-700 text-center">
                Digital automotive marketplace solutions, vehicle management
                systems, and automobile trading platforms.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <div className="h-16 w-16 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-6">
                <svg
                  className="h-8 w-8 text-orange-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900 text-center">
                Logistics Services
              </h3>
              <p className="text-gray-700 text-center">
                Advanced logistics and supply chain management solutions,
                delivery platforms, and transportation technology.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Start Your Project?
            </h2>
            <p className="text-xl text-green-100 mb-8">
              Let's discuss your requirements and create a custom solution that
              drives your business forward.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="bg-white text-primary px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                Get Started Today
              </a>
              <a
                href="/about"
                className="border border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/10 transition-colors"
              >
                Learn More About Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
